import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function PublicDisputeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, openAuth } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/complaints/public/${id}`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="ca-container py-16 text-slate-500">This dispute is not publicly visible.</div>;
  if (!d) return <div className="ca-container py-16 text-slate-500">Loading…</div>;

  return (
    <div className="ca-container py-10 max-w-3xl">
      <Link to="/disputes" className="inline-flex items-center gap-1.5 text-sm text-teal mb-6"><ArrowLeft className="h-4 w-4" /> Back to feed</Link>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-mono text-xs text-slate-400">{d.complaint_code}</p>
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">{d.primary_category || d.categories?.[0]} dispute</h1>
          <p className="text-sm text-slate-500">
            {d.party_type === "about_vendor" ? "Complaint about a vendor" : "Complaint about a customer"} by {d.author}{d.city ? ` · ${d.city}` : ""}
          </p>
        </div>
        <StatusBadge status={d.status} />
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <p className="text-xs text-slate-400 mb-1">Neutral summary</p>
        <p className="text-slate-700">{d.summary}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(d.categories || []).map((x) => <span key={x} className="rounded-full bg-teal-brand/10 text-teal px-2 py-0.5 text-xs">{x}</span>)}
        </div>
        {d.amount_range && <p className="mt-3 text-xs text-slate-500">Amount involved: <span className="font-medium text-slate-700">{d.amount_range}</span> (approximate range)</p>}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <p className="font-semibold text-slate-900 mb-4">Resolution progress</p>
        <ol className="space-y-3">
          {d.timeline.map((t, i) => (
            <li key={i} className="flex items-start gap-3">
              {t.done ? <CheckCircle2 className="h-5 w-5 text-teal shrink-0" /> : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
              <div>
                <p className={`text-sm font-medium ${t.done ? "text-slate-900" : "text-slate-400"}`}>{t.label}</p>
                <p className="text-xs text-slate-500">{t.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-6 rounded-2xl bg-brand-navy text-white p-6 text-center">
        <p className="font-semibold">Is this complaint about you?</p>
        <p className="text-slate-300 text-sm mt-1 mb-4">You have a right to reply. Complaints are allegations until verified or mutually resolved.</p>
        <Button className="bg-teal-brand hover:bg-teal-700 text-white" data-testid="public-respond-btn"
          onClick={() => (user ? navigate("/dashboard") : openAuth("login"))}>
          Respond / Challenge
        </Button>
      </div>

      <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-500">
        Private evidence, full mobile numbers, addresses and messages are never shown here. This is not a legal judgment.
      </div>
    </div>
  );
}
